import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Shield,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ClipboardList,
  Package,
  Search,
  Plus,
  Eye,
  MoreVertical,
  FileText,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/useToast';

export default function QualityControlPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('lots');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLot, setSelectedLot] = useState<any>(null);
  const [showLotDetails, setShowLotDetails] = useState(false);
  const [showChecklistDialog, setShowChecklistDialog] = useState(false);
  const [showQuarantineDialog, setShowQuarantineDialog] = useState(false);

  // Fetch QC lots
  const { data: lots = [], isLoading: lotsLoading } = useQuery({
    queryKey: ['qc-lots', statusFilter],
    queryFn: async () => {
      const params: any = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      return api.getQCLots(params);
    },
  });

  // Fetch checklists
  const { data: checklists = [] } = useQuery({
    queryKey: ['qc-checklists'],
    queryFn: () => api.getQCChecklists(),
  });

  // Fetch quarantine items
  const { data: quarantineItems = [] } = useQuery({
    queryKey: ['quarantine-items'],
    queryFn: () => api.getQuarantineItems(),
  });

  // Fetch QC stats
  const { data: stats } = useQuery({
    queryKey: ['qc-stats'],
    queryFn: () => api.getQCStats(),
  });

  // Update lot status mutation
  const updateLotMutation = useMutation({
    mutationFn: ({ lotId, status }: { lotId: string; status: string }) =>
      api.updateQCLotStatus(lotId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qc-lots'] });
      queryClient.invalidateQueries({ queryKey: ['qc-stats'] });
      toast({ title: 'Status updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Update quarantine mutation
  const updateQuarantineMutation = useMutation({
    mutationFn: ({ id, action, disposalMethod, notes }: any) =>
      api.updateQuarantineItem(id, action, disposalMethod, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quarantine-items'] });
      queryClient.invalidateQueries({ queryKey: ['qc-stats'] });
      toast({ title: 'Action completed successfully' });
      setShowQuarantineDialog(false);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Filter lots
  const filteredLots = useMemo(() => {
    if (!lots?.data) return [];
    return lots.data.filter((lot: any) =>
      lot.lot_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lot.source_number?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [lots, searchQuery]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'passed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'conditional': return 'bg-yellow-100 text-yellow-800';
      case 'pending': return 'bg-gray-100 text-gray-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const lotColumns = [
    {
      accessorKey: 'lot_number',
      header: 'Lot Number',
      cell: ({ row }: any) => (
        <div className="font-medium">{row.lot_number}</div>
      ),
    },
    {
      accessorKey: 'source_type',
      header: 'Source',
      cell: ({ row }: any) => (
        <Badge variant="outline">{row.source_type}</Badge>
      ),
    },
    {
      accessorKey: 'source_number',
      header: 'Reference',
      cell: ({ row }: any) => row.source_number || '-',
    },
    {
      accessorKey: 'inspection_status',
      header: 'Status',
      cell: ({ row }: any) => (
        <Badge className={getStatusColor(row.inspection_status)}>
          {row.inspection_status}
        </Badge>
      ),
    },
    {
      accessorKey: 'lot_quantity',
      header: 'Quantity',
      cell: ({ row }: any) => row.lot_quantity?.toLocaleString() || '-',
    },
    {
      accessorKey: 'accepted_quantity',
      header: 'Accepted',
      cell: ({ row }: any) => (
        <span className="text-green-600">{row.accepted_quantity?.toLocaleString() || 0}</span>
      ),
    },
    {
      accessorKey: 'rejected_quantity',
      header: 'Rejected',
      cell: ({ row }: any) => (
        <span className="text-red-600">{row.rejected_quantity?.toLocaleString() || 0}</span>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }: any) => new Date(row.created_at).toLocaleDateString(),
    },
    {
      id: 'actions',
      cell: ({ row }: any) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => {
              setSelectedLot(row);
              setShowLotDetails(true);
            }}>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
            {row.inspection_status === 'pending' && (
              <DropdownMenuItem onClick={() => updateLotMutation.mutate({ lotId: row.lot_id, status: 'in_progress' })}>
                <ClipboardList className="mr-2 h-4 w-4" />
                Start Inspection
              </DropdownMenuItem>
            )}
            {row.inspection_status === 'in_progress' && (
              <>
                <DropdownMenuItem onClick={() => updateLotMutation.mutate({ lotId: row.lot_id, status: 'passed' })}>
                  <CheckCircle2 className="mr-2 h-4 w-4 text-green-600" />
                  Mark as Passed
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => updateLotMutation.mutate({ lotId: row.lot_id, status: 'failed' })}>
                  <XCircle className="mr-2 h-4 w-4 text-red-600" />
                  Mark as Failed
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Shield className="h-8 w-8" />
            Quality Control
          </h1>
          <p className="text-muted-foreground">
            Inspect incoming goods and manage quality standards
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowChecklistDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Checklist
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Lots</p>
                <p className="text-3xl font-bold">{stats?.total_lots || 0}</p>
              </div>
              <ClipboardList className="h-10 w-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pass Rate</p>
                <p className="text-3xl font-bold">{stats?.pass_rate || 0}%</p>
              </div>
              <CheckCircle2 className="h-10 w-10 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Items in Quarantine</p>
                <p className="text-3xl font-bold">{stats?.items_in_quarantine || 0}</p>
              </div>
              <AlertTriangle className="h-10 w-10 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Rejected</p>
                <p className="text-3xl font-bold">{stats?.total_rejected || 0}</p>
              </div>
              <XCircle className="h-10 w-10 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="lots">Inspection Lots</TabsTrigger>
          <TabsTrigger value="checklists">Checklists</TabsTrigger>
          <TabsTrigger value="quarantine">Quarantine</TabsTrigger>
        </TabsList>

        <TabsContent value="lots" className="space-y-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle>Inspection Lots</CardTitle>
                <div className="flex gap-2">
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search lots..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="passed">Passed</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="conditional">Conditional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={lotColumns}
                data={filteredLots}
                isLoading={lotsLoading}
                searchKey="lot_number"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="checklists" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Inspection Checklists</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {checklists?.map((checklist: any) => (
                  <Card key={checklist.checklist_id} className="hover:shadow-md transition-shadow">
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold">{checklist.checklist_name}</h3>
                          <Badge variant="outline" className="mt-1">{checklist.checklist_type}</Badge>
                        </div>
                        <FileText className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        {checklist.description || 'No description'}
                      </p>
                      <div className="flex items-center justify-between mt-4">
                        <span className="text-xs text-muted-foreground">
                          {checklist.items?.length || 0} criteria
                        </span>
                        <Button variant="ghost" size="sm">View</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {checklists?.length === 0 && (
                  <div className="col-span-full text-center py-8 text-muted-foreground">
                    No checklists found. Create one to get started.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="quarantine" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Quarantine Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {quarantineItems?.map((item: any) => (
                  <div key={item.quarantine_id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-4">
                      <AlertTriangle className="h-8 w-8 text-yellow-500" />
                      <div>
                        <p className="font-medium">{item.item_name || 'Unknown Item'}</p>
                        <p className="text-sm text-muted-foreground">
                          {item.quarantine_reason} • {item.quantity} units • {item.location_name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge>{item.status}</Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedLot(item);
                          setShowQuarantineDialog(true);
                        }}
                      >
                        Process
                      </Button>
                    </div>
                  </div>
                ))}
                {quarantineItems?.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No items in quarantine
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Lot Details Sheet */}
      <Sheet open={showLotDetails} onOpenChange={setShowLotDetails}>
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Inspection Lot Details</SheetTitle>
          </SheetHeader>
          {selectedLot && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Lot Number</p>
                  <p className="font-medium">{selectedLot.lot_number}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge className={getStatusColor(selectedLot.inspection_status)}>
                    {selectedLot.inspection_status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Source</p>
                  <p className="font-medium">{selectedLot.source_type}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Reference</p>
                  <p className="font-medium">{selectedLot.source_number || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Quantity</p>
                  <p className="font-medium">{selectedLot.lot_quantity?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Inspected</p>
                  <p className="font-medium">{selectedLot.inspected_quantity?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Accepted</p>
                  <p className="font-medium text-green-600">{selectedLot.accepted_quantity?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Rejected</p>
                  <p className="font-medium text-red-600">{selectedLot.rejected_quantity?.toLocaleString()}</p>
                </div>
              </div>
              {selectedLot.notes && (
                <div>
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p>{selectedLot.notes}</p>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Checklist Dialog */}
      <Dialog open={showChecklistDialog} onOpenChange={setShowChecklistDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Inspection Checklist</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Checklist Name</label>
              <Input placeholder="Enter checklist name" />
            </div>
            <div>
              <label className="text-sm font-medium">Type</label>
              <Select defaultValue="general">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="pharmaceutical">Pharmaceutical</SelectItem>
                  <SelectItem value="food">Food</SelectItem>
                  <SelectItem value="medical_device">Medical Device</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input placeholder="Enter description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChecklistDialog(false)}>
              Cancel
            </Button>
            <Button>Create Checklist</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quarantine Dialog */}
      <Dialog open={showQuarantineDialog} onOpenChange={setShowQuarantineDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Process Quarantine Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedLot && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium">{selectedLot.item_name}</p>
                <p className="text-sm text-muted-foreground">
                  Quantity: {selectedLot.quantity} • Reason: {selectedLot.quarantine_reason}
                </p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Action</label>
              <Select defaultValue="release">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="release">Release to Inventory</SelectItem>
                  <SelectItem value="dispose">Dispose</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Notes</label>
              <Input placeholder="Enter notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuarantineDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedLot) {
                  updateQuarantineMutation.mutate({
                    id: selectedLot.quarantine_id,
                    action: 'release',
                  });
                }
              }}
            >
              Process
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
